export interface GameRTPProfilePayload {
    game_id: number;
    profile: string | number;
    default_rtp_profile?: string | number;
    force?: boolean;
    game_name?: string;
}

export interface RTPProfilesPayload {
    game_rtp_profiles: GameRTPProfilePayload[];
}

const GAME_TYPES = {
    0: "Bingo",
    1: "Spins",
    2: "Lotto",
    3: "Keno"
};

const getSelectedGameInstanceIdentifier = gameInstance =>
    gameInstance && typeof gameInstance === "object"
        ? gameInstance.identifier
        : gameInstance;

export const getRTPProfileValue = profile =>
    `${profile.version_string}${profile.version_number}`;

export const isRTPProfileGameSelected = (
    setting,
    selectedGameInstances,
    selectedGroupType = "game"
) => {
    if (selectedGroupType === "terminal") return true;

    const selectedIdentifiers = selectedGameInstances.map(
        getSelectedGameInstanceIdentifier
    );
    const gameInstanceIdentifiers = [
        setting.identifier,
        ...(setting.gameInstanceIdentifiers || [])
    ];

    return gameInstanceIdentifiers.some(identifier =>
        selectedIdentifiers.some(
            selectedIdentifier => `${selectedIdentifier}` === `${identifier}`
        )
    );
};

const hasAssignedRTPProfile = setting =>
    setting.profile !== null &&
    setting.profile !== undefined &&
    `${setting.profile}`.trim() !== "";

export const areAllSelectedGamesAssignedRTPProfile = (
    settings,
    selectedGameInstances,
    selectedGameTypes,
    selectedGroupType = "game"
) => {
    if (selectedGameTypes.length === 0) return false;

    const applicableSettings = settings.filter(setting =>
        selectedGameTypes.includes(GAME_TYPES[setting.game.type])
    );
    // Terminal groups apply to all games of the enabled types.
    if (selectedGroupType === "terminal") {
        return (
            applicableSettings.length > 0 &&
            applicableSettings.every(hasAssignedRTPProfile)
        );
    }

    const applicableGameInstances = selectedGameInstances.filter(
        gameInstance =>
            !gameInstance ||
            typeof gameInstance !== "object" ||
            !gameInstance.game ||
            gameInstance.game.type === undefined ||
            selectedGameTypes.includes(GAME_TYPES[gameInstance.game.type])
    );

    return (
        applicableGameInstances.length > 0 &&
        applicableGameInstances.every(gameInstance =>
            applicableSettings.some(
                setting =>
                    isRTPProfileGameSelected(setting, [gameInstance]) &&
                    hasAssignedRTPProfile(setting)
            )
        )
    );
};

export const buildRTPProfilesPayload = (
    settings,
    selectedGameInstances,
    selectedGameTypes,
    defaultRTPProfile,
    selectedGroupType = "game"
): RTPProfilesPayload => ({
    game_rtp_profiles: settings
        .filter(
            setting =>
                selectedGameTypes.includes(GAME_TYPES[setting.game.type]) &&
                isRTPProfileGameSelected(
                    setting, selectedGameInstances, selectedGroupType
                )
        )
        .map(setting => ({
            game_id: setting.identifier,
            profile: setting.profile,
            default_rtp_profile:
                defaultRTPProfile &&
                typeof defaultRTPProfile === "object"
                    ? defaultRTPProfile.version_number
                    : defaultRTPProfile
        }))
        .sort((first, second) => first.game_id - second.game_id)
});

export const normalizeRTPProfilesPayload = payload => {
    if (!payload) {
        return {game_rtp_profiles: []};
    }

    const gameProfiles = Array.isArray(payload)
        ? payload.reduce(
              (profiles, item) =>
                  profiles.concat(item.game_rtp_profiles || [item]),
              []
          )
        : payload.game_rtp_profiles || [];

    return {
        game_rtp_profiles: gameProfiles
            .filter(profile => profile.game_id !== undefined)
            .sort((first, second) => first.game_id - second.game_id)
    };
};

export const addGameNamesToRTPProfiles = (
    payload,
    gameSettings = []
): GameRTPProfilePayload[] =>
    normalizeRTPProfilesPayload(payload).game_rtp_profiles.map(profile => {
        const gameSetting = Array.isArray(gameSettings)
            ? gameSettings.find(setting => {
                  const settingId =
                      setting && setting.id !== undefined
                          ? setting.id
                          : setting && setting.identifier !== undefined
                          ? setting.identifier
                          : setting && setting.game_id;

                  return `${settingId}` === `${profile.game_id}`;
              })
            : null;
        const gameName =
            gameSetting &&
            (gameSetting.name ||
                gameSetting.label ||
                gameSetting.description ||
                (gameSetting.game && gameSetting.game.name));

        return {
            ...profile,
            game_name: gameName || `Game ${profile.game_id}`
        };
    });

export const getSavedRTPProfile = (payload, gameId, profiles = []) => {
    const savedProfile = normalizeRTPProfilesPayload(
        payload
    ).game_rtp_profiles.find(
        profile => `${profile.game_id}` === `${gameId}`
    );

    if (!savedProfile || savedProfile.profile === undefined) return null;

    const matchingProfile = profiles.find(
        profile =>
            getRTPProfileValue(profile) === `${savedProfile.profile}` ||
            `${profile.version_number}` === `${savedProfile.profile}`
    );

    return matchingProfile
        ? getRTPProfileValue(matchingProfile)
        : savedProfile.profile;
};

export const getSavedRTPDefaultProfile = (
    payload,
    legacyDefaultProfile = null
) => {
    const savedProfile = normalizeRTPProfilesPayload(
        payload
    ).game_rtp_profiles.find(
        profile => profile.default_rtp_profile !== undefined
    );

    return savedProfile
        ? savedProfile.default_rtp_profile
        : legacyDefaultProfile;
};

export const isRTPDefaultProfileUnchanged = (
    currentDefaultProfile,
    savedDefaultProfile
) => {
    const currentDefault =
        currentDefaultProfile &&
        currentDefaultProfile.version_number !== undefined
            ? currentDefaultProfile.version_number
            : null;

    if (currentDefault === null) {
        return (
            savedDefaultProfile === null || savedDefaultProfile === undefined
        );
    }

    return `${currentDefault}` === `${savedDefaultProfile}`;
};
